import uuid4 from './uuid4'
import _ from 'underscore'
import contextualize from './contextualize'

function expires (hours) {
  if (!hours) {
    return null
  }

  const date = new Date()
  date.setTime(date.getTime() + (hours * 60 * 60 * 1000))
  return date.toGMTString()
}

function domain () {
  return window.location.hostname.split('.').slice(-2).join('.')
}

function setCookie (name, value, hours) {
  const pairs = [[name, value]]
  if (hours) {
    pairs.push(['expires', expires(hours)])
  }

  pairs.push(['domain', domain()])
  pairs.push(['path', '/'])

  document.cookie = pairs
    .map(pair => pair.join('='))
    .join('; ')
}

function getValue (name) {
  const regex = new RegExp(`${name}=([^\\s;]*)`, 'ig')
  const matched = regex.exec(document.cookie)

  if (matched) {
    return decodeURIComponent(matched[1])
  }

  return uuid4()
}

function toCutlassFormat (accountId, userId, events) {
  return {
    visit: {
      visitor_id: this.visitorId,
      account_id: accountId,
      user_id: userId,
      id: getValue(this.options.visitCookie),
      referrer: document.referrer,
      user_agent: navigator.userAgent,
      domain: document.location.hostname
    },
    events
  }
}

function setupCookies () {
  setCookie(
    this.options.visitCookie,
    getValue(this.options.visitCookie),
    this.options.visitTTL
  )

  setCookie(
    this.options.visitorCookie,
    getValue(this.options.visitorCookie),
    this.options.visitorTTL
  )
}

function eventProperties (el) {
  const sectionName = el.closest('*[data-section]') && el.closest('*[data-section]').getAttribute('data-section')

  return {
    tag: el.tagName.toLowerCase(),
    id: el.getAttribute('id'),
    class: el.getAttribute('class'),
    page: window.location.pathname,
    section: sectionName
  }
}

function broadside (action, data) {
  if (data.length === 0) {
    return
  }

  if (this.cutlassUrl != null) {
    try {
      const url = this.cutlassUrl + '/' + action
      const json = JSON.stringify(data)
      if (navigator.sendBeacon(url, json)) {
        this.events = []
      }
    } catch (e) {
      // sendBeacon can trigger 'TypeError: Illegal invocation ', log that to Rollbar
      // and clear out the events, in case it was due to sending invalid/too much data
      window.Rollbar?.warning('Unable to fire cutlass beacon', { events: data.events?.map(e => e.name) }, e)
      this.events = []
    }
  }
}

function eventNameBase (el) {
  return el.getAttribute('data-cutlass-event') ||
  idEventName(el) ||
  classEventName(el) ||
  tagEventName(el)
}

function idEventName (el) {
  if (el.hasAttribute('id')) {
    return 'id$' + el.getAttribute('id')
  } else {
    return null
  }
}

function classEventName (el) {
  if (el.hasAttribute('class')) {
    return 'class$' + el.getAttribute('class')
  } else {
    return null
  }
}

function tagEventName (el) {
  return 'tag$' + el.tagName.toLowerCase()
}

function handleCustomDomEvents (e) {
  switch (e.type) {
    case Events.cutlassEvent:
      this.addEvent(e.detail.name, _.omit(e.detail, 'name'))
      break
  }
}

function addDocumentListener (event, handler) {
  // Prevent duplicate listeners from being attached. Otherwise, handlers will
  // execute multiple times and send duplicate tracking events.
  if (this._documentListeners.some(listener => listener.event === event)) return

  this._documentListeners.push({ event, handler })
  document.addEventListener(event, handler)
}

const Events = {
  cutlassEvent: 'cutlass:event'
}

function cutlassEvent (element, name, data = {}) {
  const event = new CustomEvent(Events.cutlassEvent, {
    bubbles: true,
    detail: Object.assign({}, { name }, data)
  })

  element.dispatchEvent(event)
}

class Cannon {
  constructor (cutlassUrl, {
    visitCookie = 'cutlass_visit',
    visitorCookie = 'cutlass_visitor',
    visitTTL = 4, // 4 hours
    visitorTTL = 14 * 24, // 14 days
    trackingOnHold = false // Set to true to hold off actually firing events, until `allowTracking` is called
  } = {}) {
    this.cutlassUrl = cutlassUrl
    this.options = { visitCookie, visitorCookie, visitTTL, visitorTTL }
    this.accountId = null
    this.userId = null
    this.events = []
    this.canFire = !trackingOnHold
    this._documentListeners = [] // Track what we add, so we can remove it

    if (this.canFire) {
      setupCookies.call(this)
    }
  }

  addEvents (events) {
    this.events = this.events.concat(events)

    if (this.canFire) {
      broadside.call(this, 'events', toCutlassFormat.call(this, this.accountId, this.userId, this.events))
    }
  }

  addEvent (event, props) {
    this.addEvents([{
      name: event,
      properties: props,
      url: window.location.href,
      query_string: window.location.search
    }])
  }

  trackView (props) {
    const properties = {
      url: window.location.href,
      title: document.title,
      page: window.location.pathname
    }

    if (props) {
      for (const name in props) {
        if (Object.prototype.hasOwnProperty.call(props, name)) {
          properties[name] = props[name]
        }
      }
    }

    this.addEvent('$view', properties)
  }

  trackClicks () {
    addDocumentListener.call(this, 'click', (e) => {
      let target

      if (e.target.matches('a, button, input[type=submit]')) {
        target = e.target
      } else {
        target = e.target.closest('a, button, input[type=submit]')
      }

      if (target) {
        const name = eventNameBase(target) + ':click'
        const properties = eventProperties(target)
        properties.href = target.getAttribute('href')

        this.addEvent(name, properties)
      }
    })
  }

  trackSubmits () {
    addDocumentListener.call(this, 'submit', (e) => {
      if (e.target.matches('form')) {
        const properties = eventProperties(e.target)
        const name = eventNameBase(e.target) + ':submit'

        this.addEvent(name, properties)
      }
    })
  }

  trackChanges () {
    addDocumentListener.call(this, 'change', (e) => {
      if (e.target.matches('input, textarea, select')) {
        const properties = eventProperties(e.target)
        const name = eventNameBase(e.target) + ':change'

        this.addEvent(name, properties)
      }
    })
  }

  trackCustomDomEvents () {
    Object.values(Events).forEach((eventType) => {
      addDocumentListener.call(this, Events.cutlassEvent, handleCustomDomEvents.bind(this))
    })
  }

  trackAll () {
    this.trackView()
    this.trackClicks()
    this.trackSubmits()
    this.trackChanges()
    this.trackCustomDomEvents()
  }

  remove () {
    this._documentListeners?.forEach(({ event, handler }) => {
      document.removeEventListener(event, handler)
    })
  }

  /**
   * When setup with `trackingOnHold`, this unqueues all the events and allows tracking
   */
  allowTracking () {
    this.canFire = true
    setupCookies.call(this)
    broadside.call(this, 'events', toCutlassFormat.call(this, this.accountId, this.userId, this.events))
  }

  get visitorId () {
    return getValue(this.options.visitorCookie)
  }
}

export {
  Cannon,
  contextualize,
  Events,
  cutlassEvent
}

window.Cutlass = {
  uuid4,
  Cannon
}
